import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Sparkles, LogOut, Plus, Edit, Copy, ExternalLink, Archive, ArchiveRestore,
  CheckCircle2, Eye, EyeOff, KeyRound, Users, BarChart3, Loader2,
  Mail, ShieldCheck, AlertCircle,
} from "lucide-react";
import { useOrganizerAuth } from "../auth/OrganizerAuthContext";
import { orgApi } from "../lib/organizerApi";
import OrganizerOnboardingPanel from "./OrganizerOnboardingPanel";
import LiveEventTile from "./LiveEventTile";

export default function OrganizerDashboard() {
  const navigate = useNavigate();
  const { organizer, logout, refresh } = useOrganizerAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [toast, setToast] = useState("");

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2200); };

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await orgApi.get("/organizer/events", { params: { include_archived: includeArchived } });
      setEvents(data.events || []);
    } catch (_e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [includeArchived]);

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); flash(`Đã sao chép: ${label}`); }
    catch (_e) { flash("Không thể sao chép"); }
  };

  const togglePublish = async (ev) => {
    setActionBusy(`publish-${ev.slug}`);
    try {
      await orgApi.post(`/organizer/events/${ev.slug}/publish`, null, { params: { active: !ev.active } });
      flash(ev.active ? "Đã ẩn sự kiện" : "Đã xuất bản sự kiện");
      reload();
    } catch (e) {
      flash(e?.response?.data?.detail || "Thao tác thất bại");
    } finally { setActionBusy(null); }
  };

  const toggleArchive = async (ev) => {
    setActionBusy(`archive-${ev.slug}`);
    try {
      await orgApi.post(`/organizer/events/${ev.slug}/archive`, null, { params: { archived: !ev.archived } });
      flash(ev.archived ? "Đã khôi phục sự kiện" : "Đã lưu trữ sự kiện");
      reload();
    } catch (e) {
      flash(e?.response?.data?.detail || "Thao tác thất bại");
    } finally { setActionBusy(null); }
  };

  const duplicate = async (ev) => {
    setActionBusy(`dup-${ev.slug}`);
    try {
      const { data } = await orgApi.post(`/organizer/events/${ev.slug}/duplicate`);
      flash("Đã tạo bản sao");
      navigate(`/organizer/events/${data.slug}/edit`);
    } catch (e) {
      flash(e?.response?.data?.detail || "Sao chép thất bại");
    } finally { setActionBusy(null); }
  };

  const onLogout = async () => {
    try { await orgApi.post("/organizer/logout"); } catch (_e) { /* ignore */ }
    logout();
    navigate("/organizer/login", { replace: true });
  };

  const summary = useMemo(() => {
    const live = events.filter((e) => e.active && !e.archived).length;
    const totalReg = events.reduce((acc, e) => acc + (e.stats?.total || 0), 0);
    const totalChecked = events.reduce((acc, e) => acc + (e.stats?.checked || 0), 0);
    return { live, totalReg, totalChecked };
  }, [events]);

  // Phase 8.9 — pick the "featured live event" for the pit-wall tile:
  //   1. Non-archived + active with the most check-in activity today, or
  //   2. Non-archived + active with the highest total registrations,
  //   3. Fall back to the most-recent non-archived + active event.
  const featuredLiveEvent = useMemo(() => {
    const candidates = events.filter((e) => e.active && !e.archived);
    if (candidates.length === 0) return null;
    return candidates.reduce((best, ev) => {
      const bestChecked = best?.stats?.checked || 0;
      const evChecked = ev?.stats?.checked || 0;
      if (evChecked !== bestChecked) return evChecked > bestChecked ? ev : best;
      const bestTotal = best?.stats?.total || 0;
      const evTotal = ev?.stats?.total || 0;
      return evTotal > bestTotal ? ev : best;
    }, candidates[0]);
  }, [events]);

  return (
    <div className="min-h-screen bg-linen" data-testid="organizer-dashboard">
      <header className="border-b border-espresso/10 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 text-espresso">
            <Sparkles className="w-4 h-4" />
            <span className="tracking-[0.28em] uppercase text-sm">Nail Viet Club</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-espresso-muted">Đăng nhập</div>
              <div className="text-sm font-medium text-espresso" data-testid="organizer-name">{organizer?.name}</div>
            </div>
            <button onClick={onLogout} data-testid="organizer-logout"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] text-espresso-soft hover:text-espresso">
              <LogOut className="w-4 h-4" /> Đăng xuất
            </button>
            <Link to="/organizer/security" data-testid="organizer-security-link"
              className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.22em] text-espresso-soft hover:text-espresso">
              <ShieldCheck className="w-4 h-4" /> Bảo mật
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <EmailVerifyBanner organizer={organizer} onChange={refresh} />

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-espresso-muted">Dashboard</p>
            <h1 className="text-3xl sm:text-4xl font-serif text-espresso mt-1">Sự kiện của tôi</h1>
            <p className="mt-2 text-espresso-soft text-sm">
              Quản lý sự kiện, đăng ký, check-in và liên kết kiosk — tất cả trong một nơi.
            </p>
          </div>
          <Link
            to="/organizer/events/new"
            data-testid="organizer-create-event"
            className="inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Tạo sự kiện mới
          </Link>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 mb-8">
          <Stat label="Sự kiện đang chạy" value={summary.live} testId="stat-live" icon={<BarChart3 className="w-4 h-4" />} />
          <Stat label="Tổng đăng ký" value={summary.totalReg} testId="stat-registrations" icon={<Users className="w-4 h-4" />} />
          <Stat label="Đã check-in" value={summary.totalChecked} testId="stat-checkedin" icon={<CheckCircle2 className="w-4 h-4" />} />
        </div>

        {featuredLiveEvent ? <LiveEventTile event={featuredLiveEvent} /> : null}

        <OrganizerOnboardingPanel events={events} />

        <div className="flex items-center justify-between mb-4">
          <label className="inline-flex items-center gap-2 text-sm text-espresso-soft">
            <input
              type="checkbox"
              data-testid="org-include-archived"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="accent-espresso"
            />
            Hiện sự kiện đã lưu trữ
          </label>
        </div>

        {loading ? (
          <div className="text-center py-12 text-espresso-soft" data-testid="org-events-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            <p className="mt-3 text-sm">Đang tải sự kiện…</p>
          </div>
        ) : events.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid md:grid-cols-2 gap-4 sm:gap-5">
            {events.map((ev) => (
              <EventCard
                key={ev.id}
                ev={ev}
                onCopy={copy}
                onPublish={() => togglePublish(ev)}
                onArchive={() => toggleArchive(ev)}
                onDuplicate={() => duplicate(ev)}
                busyKey={actionBusy}
              />
            ))}
          </div>
        )}
      </main>

      {toast && (
        <div data-testid="org-toast" className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-espresso text-cream rounded-full px-4 py-2 text-sm shadow-lg z-50">
          {toast}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testId, icon }) {
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border border-espresso/10 p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-espresso-muted flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="text-3xl font-serif text-espresso mt-1">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl bg-white border border-espresso/10 p-10 text-center" data-testid="org-events-empty">
      <Sparkles className="w-7 h-7 text-espresso/40 mx-auto" />
      <h3 className="mt-3 text-xl font-serif text-espresso">Chưa có sự kiện nào</h3>
      <p className="text-espresso-soft text-sm mt-1">Tạo sự kiện đầu tiên của bạn để bắt đầu nhận đăng ký.</p>
      <Link
        to="/organizer/events/new"
        className="mt-5 inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-sm font-semibold"
      >
        <Plus className="w-4 h-4" /> Tạo sự kiện
      </Link>
    </div>
  );
}

function EventCard({ ev, onCopy, onPublish, onArchive, onDuplicate, busyKey }) {
  const [revealed, setRevealed] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const registrationUrl = `${origin}/events/${ev.slug}`;
  const checkinUrl = `${origin}/checkin`;
  const pin = ev.checkin_pin || "";
  const live = ev.active && !ev.archived;

  return (
    <article data-testid={`org-event-card-${ev.slug}`} className="rounded-3xl bg-white border border-espresso/10 p-5 sm:p-6 flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            {ev.archived ? (
              <Badge tone="muted">Đã lưu trữ</Badge>
            ) : live ? (
              <Badge tone="green">Đang chạy</Badge>
            ) : (
              <Badge tone="amber">Bản nháp</Badge>
            )}
          </div>
          <h3 className="text-lg font-serif text-espresso leading-tight" data-testid={`org-event-name-${ev.slug}`}>{ev.name}</h3>
          <p className="text-xs text-espresso-muted mt-1">{ev.event_date} · {ev.location}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <Stat2 label="Đăng ký" value={ev.stats?.total ?? 0} />
        <Stat2 label="Check-in" value={ev.stats?.checked ?? 0} />
        <Stat2 label="Walk-in" value={ev.stats?.walkins ?? 0} />
      </div>

      {/* Kiosk widget */}
      <div className="mt-4 rounded-2xl border border-espresso/10 bg-linen-100/40 p-3.5 space-y-2.5">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft">
          <KeyRound className="w-3 h-3" /> Truy cập kiosk
        </div>
        <FieldRow label="Mã" value={ev.event_kiosk_code || "—"} testId={`org-kiosk-code-${ev.slug}`} onCopy={() => onCopy(ev.event_kiosk_code || "", "Mã kiosk")} canCopy={!!ev.event_kiosk_code} />
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-espresso-muted font-semibold">Mã PIN</div>
            <div data-testid={`org-kiosk-pin-${ev.slug}`} className="font-mono text-sm font-semibold text-espresso truncate">
              {pin ? (revealed ? pin : "•".repeat(Math.min(pin.length, 6))) : "Chưa cấu hình"}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setRevealed((r) => !r)}
            disabled={!pin}
            data-testid={`org-reveal-pin-${ev.slug}`}
            className="inline-flex items-center gap-1 border border-espresso/15 text-espresso-soft rounded-full px-2.5 py-1 text-[10px] font-medium hover:bg-white disabled:opacity-40"
          >
            {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {revealed ? "Ẩn" : "Hiện"}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <CopyChip onClick={() => onCopy(registrationUrl, "Link đăng ký")} testId={`org-copy-reg-${ev.slug}`}>Link đăng ký</CopyChip>
          <CopyChip onClick={() => onCopy(checkinUrl, "Link check-in")} testId={`org-copy-checkin-${ev.slug}`}>Link Check-in</CopyChip>
        </div>
      </div>

      {/* Action row */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Link to={`/organizer/events/${ev.slug}/edit`} data-testid={`org-edit-${ev.slug}`}
          className="inline-flex items-center justify-center gap-1.5 border border-espresso/15 rounded-full px-3 py-2 text-xs uppercase tracking-[0.18em] text-espresso hover:bg-linen-100/60">
          <Edit className="w-3.5 h-3.5" /> Chỉnh sửa
        </Link>
        <Link to={`/organizer/events/${ev.slug}/registrations`} data-testid={`org-regs-${ev.slug}`}
          className="inline-flex items-center justify-center gap-1.5 border border-espresso/15 rounded-full px-3 py-2 text-xs uppercase tracking-[0.18em] text-espresso hover:bg-linen-100/60">
          <Users className="w-3.5 h-3.5" /> Đăng ký
        </Link>
        <button onClick={onPublish} disabled={busyKey === `publish-${ev.slug}` || ev.archived} data-testid={`org-publish-${ev.slug}`}
          className="inline-flex items-center justify-center gap-1.5 border border-espresso/15 rounded-full px-3 py-2 text-xs uppercase tracking-[0.18em] text-espresso hover:bg-linen-100/60 disabled:opacity-40">
          {live ? "Ẩn" : "Xuất bản"}
        </button>
        <button onClick={onDuplicate} disabled={busyKey === `dup-${ev.slug}`} data-testid={`org-duplicate-${ev.slug}`}
          className="inline-flex items-center justify-center gap-1.5 border border-espresso/15 rounded-full px-3 py-2 text-xs uppercase tracking-[0.18em] text-espresso hover:bg-linen-100/60 disabled:opacity-40">
          <Copy className="w-3.5 h-3.5" /> Sao chép
        </button>
        <button onClick={onArchive} disabled={busyKey === `archive-${ev.slug}`} data-testid={`org-archive-${ev.slug}`}
          className="col-span-2 inline-flex items-center justify-center gap-1.5 border border-espresso/15 rounded-full px-3 py-2 text-xs uppercase tracking-[0.18em] text-espresso-soft hover:bg-linen-100/60 disabled:opacity-40">
          {ev.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
          {ev.archived ? "Khôi phục" : "Lưu trữ"}
        </button>
      </div>

      <a href={`/events/${ev.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`org-open-${ev.slug}`}
        className="mt-3 inline-flex items-center justify-center gap-1 text-[10px] uppercase tracking-[0.22em] text-espresso-muted hover:text-espresso">
        <ExternalLink className="w-3 h-3" /> Mở trang công khai
      </a>
    </article>
  );
}

function Stat2({ label, value }) {
  return (
    <div className="rounded-xl bg-linen-100/40 px-2 py-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-espresso-muted">{label}</div>
      <div className="text-lg font-serif text-espresso">{value}</div>
    </div>
  );
}

function FieldRow({ label, value, testId, onCopy, canCopy }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-espresso-muted font-semibold">{label}</div>
        <div data-testid={testId} className="font-mono text-sm font-semibold text-espresso truncate">{value}</div>
      </div>
      <button type="button" onClick={onCopy} disabled={!canCopy}
        className="inline-flex items-center gap-1 border border-espresso/15 text-espresso-soft rounded-full px-2.5 py-1 text-[10px] font-medium hover:bg-white disabled:opacity-40">
        <Copy className="w-3 h-3" /> Sao chép
      </button>
    </div>
  );
}

function CopyChip({ children, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="inline-flex items-center justify-center gap-1 border border-espresso/15 text-espresso-soft rounded-lg px-2 py-1 text-[10px] font-medium hover:bg-white">
      <Copy className="w-3 h-3" /> {children}
    </button>
  );
}

function Badge({ children, tone }) {
  const cls = tone === "green"
    ? "bg-[#E1EDE6] text-[#2D4A3E]"
    : tone === "amber"
    ? "bg-[#FBEFD0] text-[#7A5C16]"
    : "bg-[#EAE5D9] text-espresso-soft";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide ${cls}`}>
      {children}
    </span>
  );
}

function EmailVerifyBanner({ organizer, onChange }) {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  if (!organizer) return null;
  if (organizer.verified_email_at) return null; // already verified — hide
  const email = organizer.contact_email;
  if (!email) return null;

  const resend = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await orgApi.post("/organizer/verify-email/send", {});
      if (data.already_verified) { await onChange?.(); }
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Không thể gửi email xác minh.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="email-verify-banner"
      className="mb-6 rounded-3xl border border-amber-200 bg-amber-50/70 p-4 sm:p-5 flex items-start gap-3"
    >
      <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-amber-900">
          Xác minh email <span className="font-mono">{email}</span> để mở khóa toàn bộ tính năng
        </div>
        <p className="mt-1 text-xs text-amber-800">
          Chúng tôi đã gửi email xác minh khi bạn đăng ký. Hãy kiểm tra hộp thư hoặc gửi lại nếu chưa nhận được.
        </p>
        {sent && (
          <div data-testid="email-verify-sent" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" /> Đã gửi email xác minh. Vui lòng kiểm tra hộp thư.
          </div>
        )}
        {error && (
          <div data-testid="email-verify-banner-error" className="mt-2 text-xs text-rose-700">{error}</div>
        )}
      </div>
      <button
        type="button"
        onClick={resend}
        disabled={busy}
        data-testid="email-verify-banner-resend"
        className="inline-flex items-center gap-1.5 bg-espresso text-cream rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold disabled:opacity-60 whitespace-nowrap"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
        {busy ? "Đang gửi…" : "Gửi lại"}
      </button>
    </div>
  );
}
